/**
 * Suscribir esta tablet a los avisos.
 *
 * Lo que sale de aquí hacia el servidor es una dirección de push que le da el
 * navegador, la zona horaria del dispositivo y unas horas. Nada más: ni nombres,
 * ni tareas, ni quién vive en esta casa. Y lo que vuelve por esa dirección es
 * una notificación sin contenido, porque el texto lo pone el service worker.
 *
 * En Android esa dirección es de Google, y no hay manera de que no lo sea: es la
 * única forma de que llegue algo con la app cerrada. Lo que sí está en nuestra
 * mano es que por ahí no viaje nada, y es lo que se ha hecho.
 */

export interface Aviso {
  readonly hora: string
  readonly previo: boolean
}

export type AvisosFailure = 'sin-permiso' | 'sin-soporte' | 'sin-servidor' | 'sin-red'

export type AvisosResult =
  | { readonly ok: true; readonly avisos: number }
  | { readonly ok: false; readonly failure: AvisosFailure }

function bytes(base64url: string): Uint8Array {
  const relleno = '='.repeat((4 - (base64url.length % 4)) % 4)
  const crudo = atob((base64url + relleno).replace(/-/g, '+').replace(/_/g, '/'))
  const salida = new Uint8Array(crudo.length)
  for (let i = 0; i < crudo.length; i++) salida[i] = crudo.charCodeAt(i)
  return salida
}

async function registro(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) return undefined
  return navigator.serviceWorker.getRegistration()
}

/** Qué hay puesto ahora mismo en esta tablet, para poder enseñarlo. */
export async function suscripcionActual(): Promise<PushSubscription | null> {
  const reg = await registro()
  if (!reg) return null
  return reg.pushManager.getSubscription().catch(() => null)
}

export async function guardarAvisos(
  apiUrl: string,
  token: string | undefined,
  avisos: readonly Aviso[],
): Promise<AvisosResult> {
  if (apiUrl.trim().length === 0) return { ok: false, failure: 'sin-servidor' }

  const reg = await registro()
  if (!reg) return { ok: false, failure: 'sin-soporte' }

  if (!('Notification' in window)) return { ok: false, failure: 'sin-soporte' }
  if ((await Notification.requestPermission()) !== 'granted') {
    return { ok: false, failure: 'sin-permiso' }
  }

  const cabeceras = {
    'Content-Type': 'application/json',
    ...(token !== undefined && token.length > 0 ? { Authorization: `Bearer ${token}` } : {}),
  }

  try {
    const clave = (await (await fetch(`${apiUrl.replace(/\/+$/, '')}/avisos/clave`)).json()) as {
      clave?: string
      listo?: boolean
    }
    if (clave.listo !== true || typeof clave.clave !== 'string' || clave.clave.length === 0) {
      return { ok: false, failure: 'sin-servidor' }
    }

    // Se reutiliza la suscripción que ya hubiera. Volver a suscribirse cambia la
    // dirección y dejaría una muerta en el servidor.
    const suscripcion =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        // Obligatorio en Chrome: promete que cada push produce algo visible. Y
        // es verdad, porque el worker siempre muestra una notificación.
        userVisibleOnly: true,
        applicationServerKey: bytes(clave.clave) as BufferSource,
      }))

    const response = await fetch(`${apiUrl.replace(/\/+$/, '')}/avisos/suscribir`, {
      method: 'POST',
      headers: cabeceras,
      body: JSON.stringify({
        endpoint: suscripcion.endpoint,
        // La zona del dispositivo, para que las nueve sean las nueve de aquí y
        // no las del servidor.
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        avisos,
      }),
    })

    if (response.status === 401) return { ok: false, failure: 'sin-servidor' }
    if (!response.ok) return { ok: false, failure: 'sin-red' }

    const cuerpo = (await response.json()) as { avisos?: unknown }
    return { ok: true, avisos: typeof cuerpo.avisos === 'number' ? cuerpo.avisos : 0 }
  } catch {
    return { ok: false, failure: 'sin-red' }
  }
}
